/**
 * ORR-233 — Releer el padrón de ARCA para varios clientes de una.
 *
 * El caso de uso que pidió Lisandro es semestral: después de una recategorización de
 * monotributo, releer la categoría de todos en vez de entrar cliente por cliente.
 *
 * Corre **desde el front, secuencial**, reusando la Netlify function que ya existe
 * (un CUIT por llamada). No hay backend nuevo a propósito: 68 clientes son 70-140 s y
 * una function síncrona de Netlify se corta a los 15 s, pero una background function
 * con tabla de progreso y polling son ~400 líneas de infra para una acción que se
 * dispara dos veces al año y que el contador mira mientras corre.
 *
 * Consecuencia asumida: si se cierra la pestaña, el lote se corta. No deja nada
 * inconsistente —cada cliente se escribe entero o no se escribe— y volver a apretar
 * el botón retoma desde donde estaba, porque releer un cliente ya actualizado no
 * cambia nada.
 */

import { useCallback, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useAuth0 } from '@auth0/auth0-react'

import { useSupabase } from '@/lib/supabase-context'
import { useOrganizacion } from '@/core/providers/OrganizacionProvider'
import { usePermissions } from '@/hooks/usePermissions'
import { useConsultarCuitAFIP } from '@/hooks/useConsultarCuitAFIP'
import { clientesService } from '@/services/clientesService'
import { clientesKeys } from '@/hooks/useClientes'
import { assertPermiso } from '@/lib/permisos-guard'
import { padronToFacetaPatch } from '@/hooks/useSincronizarPadronCliente'
import { notificarCambiosCategoria, type CambioCategoria } from '@/hooks/notificarCambioCategoria'
import { fetchInChunks } from '@/lib/postgrest-chunks'

/** Pausa entre consultas. ARCA no publica su rate limit; 300ms es cortesía, no un límite medido. */
const DELAY_ENTRE_CONSULTAS_MS = 300

export interface ClienteASincronizar {
  id: string
  nombre: string
  cuit: string | null
}

export type ResultadoTipo = 'actualizado' | 'sin_cambios' | 'no_encontrado' | 'error'

export interface ResultadoSincronizacion {
  clienteId: string
  nombre: string
  tipo: ResultadoTipo
  /** Solo en 'actualizado': qué categoría pasó a tener. */
  categoriaNueva?: string | null
  /** Solo en 'actualizado': qué categoría tenía antes. NULL = no tenía. */
  categoriaAnterior?: string | null
  /** Solo en 'error': el mensaje, para que el grupo no quede en silencio. */
  detalle?: string
}

export function useSincronizarPadronMasivo() {
  const { supabase } = useSupabase()
  const { organizacionId } = useOrganizacion()
  const { puede } = usePermissions()
  const queryClient = useQueryClient()
  const { consultarCuitAsync } = useConsultarCuitAFIP()
  const { user } = useAuth0()

  const [corriendo, setCorriendo] = useState(false)
  const [procesados, setProcesados] = useState(0)
  const [total, setTotal] = useState(0)
  const [resultados, setResultados] = useState<ResultadoSincronizacion[]>([])

  // Permite cortar el lote sin dejar a medias el cliente en curso: se chequea
  // entre iteraciones, nunca en el medio de una escritura.
  const cancelado = useRef(false)

  const cancelar = useCallback(() => {
    cancelado.current = true
  }, [])

  const sincronizar = useCallback(
    async (clientes: ClienteASincronizar[]): Promise<ResultadoSincronizacion[]> => {
      assertPermiso(puede, 'sincronizarPadron')
      if (!supabase) throw new Error('Cliente Supabase no disponible')
      if (!organizacionId) throw new Error('Sin organización activa')

      cancelado.current = false
      setCorriendo(true)
      setProcesados(0)
      setTotal(clientes.length)
      setResultados([])

      // La categoría de HOY, para poder decir cuál cambió y cuál no. Se resuelve acá y
      // no la pasa el caller: `vista_saldos_clientes` —la que alimenta la pantalla de
      // Clientes— no expone `categoria_monotributo`, y agregarla obligaría a recrear una
      // vista de la que depende media app para un dato que solo necesita este lote.
      const filas = await fetchInChunks(
        clientes.map((c) => c.id),
        async (batch) => {
          const { data, error } = await supabase
            .from('cliente_faceta_contable')
            .select('cliente_id, categoria_monotributo')
            .in('cliente_id', batch)
          if (error) throw error
          return data ?? []
        }
      )
      const categoriaPrevia = new Map<string, string | null>(
        filas.map((f) => [f.cliente_id as string, f.categoria_monotributo as string | null])
      )

      const acumulados: ResultadoSincronizacion[] = []

      for (const cliente of clientes) {
        if (cancelado.current) break

        try {
          if (!cliente.cuit) {
            acumulados.push({
              clienteId: cliente.id,
              nombre: cliente.nombre,
              tipo: 'error',
              detalle: 'Sin CUIT cargado',
            })
            continue
          }

          const data = await consultarCuitAsync(String(cliente.cuit))

          if (!data.encontrado || !data.persona) {
            acumulados.push({ clienteId: cliente.id, nombre: cliente.nombre, tipo: 'no_encontrado' })
            continue
          }

          const patch = padronToFacetaPatch(data.persona)

          if (Object.keys(patch).length === 0) {
            // El padrón respondió pero sin ningún dato utilizable. No es un error del
            // lote, pero tampoco es "sin cambios": no se escribió nada.
            acumulados.push({
              clienteId: cliente.id,
              nombre: cliente.nombre,
              tipo: 'no_encontrado',
            })
            continue
          }

          await clientesService.actualizarFacetaContable(supabase, cliente.id, organizacionId, patch)

          const categoriaNueva = (patch.categoria_monotributo as string | undefined) ?? null
          const categoriaAnterior = categoriaPrevia.get(cliente.id) ?? null
          const cambioCategoria = categoriaNueva !== null && categoriaNueva !== categoriaAnterior

          acumulados.push({
            clienteId: cliente.id,
            nombre: cliente.nombre,
            tipo: cambioCategoria ? 'actualizado' : 'sin_cambios',
            categoriaNueva,
            categoriaAnterior,
          })
        } catch (error) {
          // Un cliente que falla no aborta el lote: es el punto de la acción masiva.
          // Pero el error queda con nombre y motivo — el grupo que no puede quedar
          // en silencio es justamente este.
          acumulados.push({
            clienteId: cliente.id,
            nombre: cliente.nombre,
            tipo: 'error',
            detalle: error instanceof Error ? error.message : 'Error desconocido',
          })
        } finally {
          setProcesados((n) => n + 1)
          setResultados([...acumulados])
        }

        if (DELAY_ENTRE_CONSULTAS_MS > 0) {
          await new Promise((r) => setTimeout(r, DELAY_ENTRE_CONSULTAS_MS))
        }
      }

      queryClient.invalidateQueries({ queryKey: clientesKeys.all })

      // ORR-351: UN aviso con el resumen, no uno por cliente. Con lo actualizado hasta acá
      // aunque el lote se haya cancelado: esos cambios sí se escribieron. Nunca lanza.
      await notificarCambiosCategoria(supabase, user?.email, cambiosDelLote(acumulados), user?.name)

      setCorriendo(false)
      return acumulados
    },
    [supabase, organizacionId, puede, consultarCuitAsync, queryClient, user?.email, user?.name]
  )

  return { sincronizar, cancelar, corriendo, procesados, total, resultados }
}

/** Agrupa para el resumen final. Los 4 grupos se muestran siempre, incluso en cero. */
export function resumirResultados(resultados: ResultadoSincronizacion[]) {
  return {
    actualizados: resultados.filter((r) => r.tipo === 'actualizado'),
    sinCambios: resultados.filter((r) => r.tipo === 'sin_cambios'),
    noEncontrados: resultados.filter((r) => r.tipo === 'no_encontrado'),
    errores: resultados.filter((r) => r.tipo === 'error'),
    total: resultados.length,
  }
}

/** Pura. Los cambios de categoría efectivos de un lote, para el aviso de resumen (ORR-351). */
export const cambiosDelLote = (resultados: ResultadoSincronizacion[]): CambioCategoria[] =>
  resultados.flatMap((r) =>
    r.tipo === 'actualizado' && typeof r.categoriaNueva === 'string'
      ? [{ clienteId: r.clienteId, nombre: r.nombre, anterior: r.categoriaAnterior ?? null, nueva: r.categoriaNueva }]
      : []
  )
